import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import StringUtils from "@/utils/StringUtils";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import { RootState } from "@/types/RootState";
import { SearchDataCategory } from "./types";

export const SectionLabel = styled.div`
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  opacity: 0.5;
  letter-spacing: 0.03em;
`;

export const SearchResultItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  background: transparent;
  font-size: 13px;
  font-weight: normal;
  color: #424b59;
  text-decoration: none;

  &:hover {
    background: #f9fafb;
  }
`;

export const ItemIcon = styled.div`
  flex-shrink: 0;
  margin-right: 10px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  img,
  svg {
    width: 22px;
    height: 22px;
  }
`;

export const ItemSub = styled.div`
  font-size: 11px;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * Backend search categories are labelled through the
 * `search_category_{name}_name` key convention, falling back to the raw
 * category name when no key exists. Shared with the panel's category rail, so
 * a rail entry and its section header can never disagree.
 */
export const translateSearchCategory = (name: string): string => {
  const categoryKey = `search_category_${name}_name` as LanguageKey;
  const translatedCategory = translate(categoryKey);
  return translatedCategory !== categoryKey ? translatedCategory : name;
};

interface SearchItemProps {
  dataSearch: SearchDataCategory;
  closePopup: () => void;
  isRecent: boolean;
  strSearch?: string;
  /**
   * Render the whole group instead of the first four with a "view all" link.
   * Set when the panel's category rail has narrowed the results to this group
   * on its own — asking for one category and still getting four of it, behind
   * a second link, is a cap with nothing left to protect.
   */
  showAll?: boolean;
  /**
   * What the "view all matching …" link does. Supplied by the panel when the
   * category rail is on screen, where the link selects this group's rail entry
   * — the state that already means "all of this category". Left undefined when
   * there is no rail (mobile), and the link falls back to expanding in place,
   * because a rail selection there could not be undone.
   */
  onViewAll?: () => void;
}

function SearchItem({
  dataSearch,
  closePopup,
  isRecent,
  strSearch,
  showAll,
  onViewAll,
}: SearchItemProps) {
  const history = useHistory();
  const loginInfo = useSelector((state: RootState) => state.user);
  const showOrgName =
    loginInfo?.organisation?.organisationType === "SYSTEM" ||
    loginInfo?.organisation?.organisationType === "PARTNER";
  // Recently-viewed and a rail-narrowed category are both already the complete
  // list the user asked for, so neither truncates.
  const showEveryItem = isRecent || showAll === true;
  const [showMoreButton, setShowMoreButton] = useState(true);
  const [list, setList] = useState(
    showEveryItem ? dataSearch?.listItem ?? [] : dataSearch?.listItem.slice(0, 4) ?? []
  );
  // Derived, not seeded into state: groups are keyed by index, so a new query
  // reuses this component with different data. Held in useState, `listData` and
  // `maxList` kept the FIRST query's items, and expanding a group then showed
  // results from a search the user had already replaced.
  const listData = dataSearch?.listItem ?? [];
  const maxList = listData.length;
  const [limitList, setLimitList] = useState(8);

  const showMore = () => {
    setList(listData.slice(0, limitList + maxList));
    setLimitList(limitList + maxList);
    if (listData.slice(0, limitList + maxList).length === maxList) {
      setShowMoreButton(false);
    }
  };

  const showLess = () => {
    setList(listData.slice(0, 4));
    setLimitList(4);
    setShowMoreButton(true);
  };

  const sectionLabel = useMemo(() => {
    if (isRecent) return dataSearch.name;
    return translateSearchCategory(dataSearch.name);
  }, [isRecent, dataSearch.name]);

  const getSubtitle = (item: typeof list[0]) => {
    const base = isRecent
      ? item?.category
      : (item?.data.subtitle || item?.data.title);
    const org = showOrgName && item?.data.orgName ? ` · ${item.data.orgName}` : "";
    return base + org;
  };

  const itemList = useMemo(() => {
    return (
      <>
        <SectionLabel>{sectionLabel}</SectionLabel>
        {list.map((item, index) => (
          <SearchResultItem
            key={index}
            // Marks this row as a target for the panel's up/down keyboard
            // navigation, which walks the rendered rows across every group.
            className="gb-kb-row"
            onClick={() => {
              history.push(item.link);
              closePopup();
            }}
            title={item?.data.title}
          >
            <ItemIcon>{item?.img}</ItemIcon>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="text-truncate">{item?.data.title}</div>
              <ItemSub>{getSubtitle(item)}</ItemSub>
            </div>
          </SearchResultItem>
        ))}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSearch, list, sectionLabel]);

  useEffect(() => {
    if (showEveryItem) {
      setList(dataSearch.listItem);
    } else {
      setList(dataSearch.listItem.slice(0, 4));
      // Selecting a rail category and going back to "All results" has to
      // re-arm the link, or the group stays expanded with no way to collapse.
      setShowMoreButton(true);
      setLimitList(8);
    }
  }, [dataSearch, dataSearch.listItem, showEveryItem]);

  if (showEveryItem) {
    return <div>{itemList}</div>;
  }

  return (
    <div>
      {itemList}
      {showMoreButton && maxList > 4 && (
        <a
          href="# "
          onClick={(e) => {
            e.preventDefault();
            if (onViewAll) {
              onViewAll();
              return;
            }
            showMore();
          }}
          style={{
            color: "#5094BE",
            fontSize: 12,
            fontWeight: "normal",
            padding: "4px 12px",
            display: "inline-block",
            textDecoration: "none",
          }}
        >
          {strSearch &&
            StringUtils.replaceStr(
              translate(LanguageKey.v41_view_all_matching_key),
              dataSearch.name.toLowerCase()
            ) +
              ' "' +
              strSearch +
              '"'}
        </a>
      )}
      {!showMoreButton && (
        <a
          href="# "
          onClick={(e) => {
            e.preventDefault();
            showLess();
          }}
          style={{
            color: "#5094BE",
            fontSize: 12,
            fontWeight: "normal",
            padding: "4px 12px",
            display: "inline-block",
            textDecoration: "none",
          }}
        >
          <u>{translate(LanguageKey.v41_show_less_key)}</u>
        </a>
      )}
    </div>
  );
}

export default SearchItem;
